/**
 * Check if user has a permission key (or any of the keys if array).
 * user.permissions is array of { key, module, action, ... } from /api/auth/me.
 */
export function hasPermission(user, keyOrKeys) {
  if (!user?.permissions?.length) return false;
  const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
  const set = new Set(user.permissions.map((p) => p.key));
  return keys.some((k) => set.has(k));
}

/** Job posting permission keys */
export const JOB_PERMISSIONS = {
  VIEW: 'job.view',
  CREATE: 'job.create',
  EDIT: 'job.edit',
  DELETE: 'job.delete',
  CLOSE: 'job.close',
  REOPEN: 'job.reopen',
};

/** Assessment permission keys */
export const ASSESSMENT_PERMISSIONS = {
  VIEW: 'assessment.view',
  CREATE: 'assessment.create',
  EDIT: 'assessment.edit',
  DELETE: 'assessment.delete',
  PUBLISH: 'assessment.publish',
  INVITE: 'assessment.invite',
  RESULTS: 'assessment.results',
};

/** AI Interview permission keys */
export const INTERVIEW_PERMISSIONS = {
  VIEW: 'interview.view',
  CREATE: 'interview.create',
  EDIT: 'interview.edit',
  DELETE: 'interview.delete',
  PUBLISH: 'interview.publish',
  INVITE: 'interview.invite',
  RESULTS: 'interview.results',
};

/** Sourcing / candidate permission keys (team scope) */
export const SOURCING_PERMISSIONS = {
  VIEW: 'sourcing.view',
};
export const CANDIDATE_PERMISSIONS = {
  VIEW: 'candidate.view',
  CREATE: 'candidate.create',
  EDIT: 'candidate.edit',
  DELETE: 'candidate.delete',
  INVITE: 'candidate.invite',
  SCORE: 'candidate.score',
};

/**
 * Org admin, matching the server's definition (utils/permissions.js, utils/jobAccess.js).
 * Deliberately NOT `useAuth().isAdmin`, which only checks `role === 'admin'` and so would
 * lock out every user carrying the equally-valid `org_admin` role.
 */
export function isOrgAdminUser(user) {
  return (
    user?.accountType === 'org' && (user?.role === 'admin' || user?.role === 'org_admin')
  );
}

/**
 * Which Reports page (if any) this user gets. The three variants share one route and one
 * sidebar entry, so resolving them in a single place keeps the route guard and the nav in
 * step and makes duplicate links impossible if an org ever carries more than one flag.
 *
 *   'gocolours' → ReportsPage      (feature `reports`)
 *   'mr'        → ReportsPageMR    (feature `reports_mr`)
 *   'bk'        → ReportsPageBK    (feature `reports_bk`: org admins; with `reports_bk_regional`
 *                                   also every org member who belongs to at least one team)
 *
 * This only decides whether the page is offered. Which regions it shows is the server's call
 * (GET /api/reports/bk/scope), made against the database rather than this cached user.
 */
export function getReportsVariant(user) {
  const features = user?.features || {};
  if (features.reports === true) return 'gocolours';
  if (features.reports_mr === true) return 'mr';
  if (features.reports_bk === true) {
    if (isOrgAdminUser(user)) return 'bk';
    if (features.reports_bk_regional === true && isRegionalOrgMember(user)) return 'bk';
  }
  return null;
}

/** An org account that belongs to at least one team — the only way to have a region. */
function isRegionalOrgMember(user) {
  return (
    user?.accountType === 'org' &&
    Array.isArray(user?.departmentIds) &&
    user.departmentIds.some((id) => Boolean(id?._id || id))
  );
}
